import { useState, useCallback } from "react";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import {
  ActiveAffiliatesTable,
  AffiliateSortBy,
  AffiliateSortOrder,
} from "../../../components/AffiliateTable/ActiveAffiliatesTable";
import { programService } from "../../../services/program.service";
import { nicheService } from "../../../services/niche.service";
import { Affiliate } from "../../../types/affiliate";
import {
  AffiliateFilterBar,
  AgeFilter,
  RevenueFilter,
  AGE_MAX,
} from "../../../components/common/AffiliateFilterBar";
import { Users } from "react-feather";
import { Link } from "react-router-dom";
import debounce from "lodash/debounce";
import PageHeader from "../../../components/common/PageHeader";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { EmptyState } from "@/components/ui/empty-state";
import { TableCard } from "@/components/ui/table-card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { brandService } from "../../../services/brand.service";

interface FilterState {
  country: string[];
  niche: string[];
  gender: "MALE" | "FEMALE" | "OTHER" | "";
  age: AgeFilter;
  revenue: RevenueFilter;
}

const defaultFilters: FilterState = {
  country: [],
  niche: [],
  gender: "",
  age: { range: null, includeNoAge: true },
  revenue: { min: null, max: null },
};

interface AffiliateParams {
  page: number;
  limit: number;
  keyword?: string;
  searchBy?: "NAME" | "EMAIL" | "CODE";
  countries?: string;
  gender?: "MALE" | "FEMALE" | "OTHER";
  niches?: string;
  minAge?: number;
  maxAge?: number;
  includeNoAge?: boolean;
  minRevenue?: number;
  maxRevenue?: number;
  sortBy?: AffiliateSortBy;
  sortOrder?: AffiliateSortOrder;
}

export function ActiveAffiliates() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<AffiliateSortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<AffiliateSortOrder>("ASC");
  const [showIncompletePostCheckout, setShowIncompletePostCheckout] =
    useState(false);

  // Fetch niches using React Query
  const { data: niches = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["niches"],
    queryFn: () => nicheService.getAvailableNiches(),
  });

  // Build query params for affiliates fetch
  const buildQueryParams = useCallback(
    (search: string, currentFilters: FilterState): AffiliateParams => {
      const params: AffiliateParams = {
        page,
        limit: pageSize,
      };

      if (search) {
        params.keyword = search;
        params.searchBy = "NAME";
      }

      if (currentFilters.country.length > 0)
        params.countries = currentFilters.country.join(",");
      if (currentFilters.gender) params.gender = currentFilters.gender;
      if (currentFilters.niche.length > 0)
        params.niches = currentFilters.niche.join(",");

      // Age is only sent once the user sets a range; the top of the slider
      // means "and older", so no upper bound is sent for it
      if (currentFilters.age.range) {
        params.minAge = currentFilters.age.range[0];
        if (currentFilters.age.range[1] < AGE_MAX)
          params.maxAge = currentFilters.age.range[1];
        params.includeNoAge = currentFilters.age.includeNoAge;
      }

      if (currentFilters.revenue.min != null)
        params.minRevenue = currentFilters.revenue.min;
      if (currentFilters.revenue.max != null)
        params.maxRevenue = currentFilters.revenue.max;

      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      return params;
    },
    [page, pageSize, sortBy, sortOrder]
  );

  // Fetch affiliates using React Query
  const {
    data: affiliatesData,
    isLoading: loading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "active-affiliates",
      page,
      pageSize,
      debouncedSearchQuery,
      filters,
      sortBy,
      sortOrder,
      showIncompletePostCheckout,
    ],
    queryFn: async () => {
      if (showIncompletePostCheckout) {
        // Fetch incomplete post-checkout affiliates using same filters/search params
        const params = buildQueryParams(debouncedSearchQuery, filters);
        const response = await brandService.getIncompletePostCheckoutAffiliates(
          params.page,
          params.limit,
          params
        );

        // Map to Affiliate-like shape for the table
        const mapped: Affiliate[] = response.affiliates.map((a) => ({
          id: a.id,
          firstName: a.name.split(" ")[0] || "",
          lastName: a.name.split(" ").slice(1).join(" "),
          email: a.email,
          code: a.code,
          revenue: a.totalSales,
          profileImage: "",
          isProfileCompleted: false,
        })) as Affiliate[];

        return {
          data: mapped,
          pagination: response.pagination,
        };
      }

      const params = buildQueryParams(debouncedSearchQuery, filters);
      return programService.findAffiliates(params);
    },
  });

  // Extract affiliates and total from query response
  const affiliates: Affiliate[] = affiliatesData
    ? Array.isArray(affiliatesData)
      ? affiliatesData
      : affiliatesData.data
    : [];
  const total = affiliatesData
    ? Array.isArray(affiliatesData)
      ? affiliatesData.length
      : affiliatesData.pagination.total
    : 0;

  // Debounced search handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setPage(1);
      setDebouncedSearchQuery(value);
    }, 500),
    []
  );

  // Handle search input
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Filter changes apply immediately; each one restarts from page 1
  const updateFilters = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setPage(1);
  };

  // Server-side sorting: the table reports TanStack sorting state, which maps
  // straight onto the sortBy/sortOrder query params. Any change restarts from
  // page 1.
  const sorting: SortingState = sortBy
    ? [{ id: sortBy, desc: sortOrder === "DESC" }]
    : [];

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    if (next.length > 0) {
      setSortBy(next[0].id as AffiliateSortBy);
      setSortOrder(next[0].desc ? "DESC" : "ASC");
    } else {
      setSortBy(null);
      setSortOrder("ASC");
    }
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Active affiliates" showDefaultActions={true} />

      {/* Filters */}
      <div
        id="filter-affiliates"
        className="mb-6 bg-card p-4 sm:p-6 rounded-xl border border-border shadow-sm"
      >
        <AffiliateFilterBar
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder="Search name or email"
          nicheOptions={niches.map((niche) => ({
            label: niche.name,
            value: niche.id,
          }))}
          niche={filters.niche}
          onNicheChange={(value) => updateFilters({ niche: value })}
          gender={filters.gender}
          onGenderChange={(value) => updateFilters({ gender: value })}
          country={filters.country}
          onCountryChange={(value) => updateFilters({ country: value })}
          age={filters.age}
          onAgeChange={(value) => updateFilters({ age: value })}
          revenue={filters.revenue}
          onRevenueChange={(value) => updateFilters({ revenue: value })}
          onResetAll={handleResetFilters}
        />
      </div>

      <TableCard>
        <UnderlineTabs
          tabs={[
            { value: "active", label: "Active program affiliates" },
            { value: "post-checkout", label: "Post-checkout" },
          ]}
          value={showIncompletePostCheckout ? "post-checkout" : "active"}
          onValueChange={(value) => {
            setShowIncompletePostCheckout(value === "post-checkout");
            setPage(1);
          }}
          className="px-4 pt-1"
        />
        {isError ? (
          <div className="text-center py-8 text-sm text-red-600">
            {error instanceof Error
              ? error.message
              : "Failed to fetch affiliates"}
          </div>
        ) : !loading && affiliates.length === 0 ? (
          <EmptyState
            bordered={false}
            icon={<Users />}
            title="No active affiliates yet"
            body="Start growing your business by inviting affiliates to join your program"
            action={
              <Button asChild>
                <Link to="/brand/affiliates/invite">Invite</Link>
              </Button>
            }
          />
        ) : (
          <ActiveAffiliatesTable
            data={affiliates}
            page={page}
            limit={pageSize}
            total={total}
            isLoading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRemoval={showIncompletePostCheckout}
            sorting={showIncompletePostCheckout ? undefined : sorting}
            onSortingChange={
              showIncompletePostCheckout ? undefined : handleSortingChange
            }
          />
        )}
      </TableCard>
    </div>
  );
}
